import React, { useState, useMemo } from 'react';
import { Card, Typography, Space, Table, Select, Badge, Button, message, Row, Col, Grid, Input, Radio, Tag } from 'antd';
import { UserOutlined, ExclamationCircleOutlined, SearchOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { remove_ga4_connectionApi } from '../../api/admin.apis';

const { Title, Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

export const ConnectionListing = ({ ga4Properties, onRemoved, onChange }) => {
    const screens = useBreakpoint();
    const [selectedProperties, setSelectedProperties] = useState({});
    const [loadingRemove, setLoadingRemove] = useState({});
    const [searchText, setSearchText] = useState('');
    const [filterStatus, setFilterStatus] = useState('a'); // 'a' for all, 'b' for active, 'c' for inactive

    // Group properties by user and initialize selected properties
    const groupedData = useMemo(() => {
        return ga4Properties?.map(userData => {
            const { user, connections } = userData;
            let allProperties = [];

            connections.forEach(connection => {
                connection.properties.forEach(property => {
                    const stream = property.streams?.[0];
                    allProperties.push({
                        id: property.id,
                        name: property.name,
                        selected: property.selected,
                        measurementId: stream?.webStreamData?.measurementId || 'N/A',
                        defaultUri: stream?.webStreamData?.defaultUri || '-',
                        account_name: connection.account_name,
                        connection_id: connection.connection_id,
                    });
                });
            });

            // Sort properties: selected first, then alphabetically
            allProperties.sort((a, b) => {
                if (a.selected && !b.selected) return -1;
                if (!a.selected && b.selected) return 1;
                return a.name.localeCompare(b.name);
            });

            const userSelectedPropertyId =
                user.selected_property_id || allProperties.find(p => p.selected)?.id || allProperties[0]?.id;

            if (!selectedProperties[user.id]) {
                setSelectedProperties(prev => ({ ...prev, [user.id]: userSelectedPropertyId }));
            }

            return {
                user: { ...user, selected_property_id: userSelectedPropertyId },
                properties: allProperties,
                selectedProperty: allProperties.find(p => p.id === (selectedProperties[user.id] || userSelectedPropertyId)) || allProperties[0],
            };
        }) || [];
    }, [ga4Properties, selectedProperties]);

    // Filter data based on search text and status filter
    const filteredData = useMemo(() => {
        let filtered = groupedData;

        // Apply status filter
        if (filterStatus === 'b') {
            // Active - users with selected property that is active
            filtered = filtered.filter(record => 
                record.selectedProperty && record.selectedProperty.selected === true
            );
        } else if (filterStatus === 'c') {
            // Inactive - users with selected property that is inactive
            filtered = filtered.filter(record => 
                record.selectedProperty && record.selectedProperty.selected === false
            );
        }

        // Apply search filter
        if (searchText.trim()) {
            const searchLower = searchText.toLowerCase();
            filtered = filtered.filter(record => {
                // Search in user name and email
                const userNameMatch = record.user.full_name.toLowerCase().includes(searchLower);
                const userEmailMatch = record.user.email.toLowerCase().includes(searchLower);
                
                // Search in property names
                const propertyMatch = record.properties.some(property => 
                    property.name.toLowerCase().includes(searchLower) ||
                    (property.defaultUri !== '-' && property.defaultUri.toLowerCase().includes(searchLower)) ||
                    property.measurementId.toLowerCase().includes(searchLower)
                );

                return userNameMatch || userEmailMatch || propertyMatch;
            });
        }

        return filtered;
    }, [groupedData, searchText, filterStatus]);

    const isDataEmpty = !ga4Properties || ga4Properties.length === 0 || filteredData.length === 0;

    const handlePropertySelect = (userId, propertyId) => {
        setSelectedProperties(prev => ({ ...prev, [userId]: propertyId }));
    };

    // FIXED: Handle Radio.Group onChange event correctly
    const handleStatusFilterChange = (e) => {
        // In Ant Design Radio.Group, the event contains the value directly
        // or it might be passed as the parameter itself
        const value = e?.target?.value ?? e;
        setFilterStatus(value);
        
        // Notify parent component about filter change
        if (typeof onChange === 'function') {
            onChange({ status: value, search: searchText });
        }
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchText(value);
        
        // Notify parent component about search change
        if (typeof onChange === 'function') {
            onChange({ status: filterStatus, search: value });
        }
    };

    const handleRemoveClick = async (user, property) => {
        const confirm = window.confirm(`Are you sure you want to remove "${property.name}" for user ${user.full_name}?`);
        if (!confirm) return;

        setLoadingRemove(prev => ({ ...prev, [user.id]: true }));

        try {
            const response = await remove_ga4_connectionApi(user.id, property.connection_id);

            if (response?.data?.status === 'success') {
                message.success('GA4 connection removed successfully!');
                onRemoved?.(); // callback to refresh list
            } else {
                message.error(response?.data?.message || 'Failed to remove GA4 connection.');
            }
        } catch (error) {
            console.error(error);
            message.error('An error occurred while removing the connection.');
        } finally {
            setLoadingRemove(prev => ({ ...prev, [user.id]: false }));
        }
    };

    const clearFilters = () => {
        setSearchText('');
        setFilterStatus('a');
        
        // Notify parent about filter clear
        if (typeof onChange === 'function') {
            onChange({ status: 'a', search: '' });
        }
    };

    // Calculate counts for radio buttons
    const activeCount = groupedData.filter(record => record.selectedProperty?.selected === true).length;
    const inactiveCount = groupedData.filter(record => record.selectedProperty?.selected === false).length;

    // Mobile card
    const MobileCard = ({ record }) => (
        <Card size="small" style={{ marginBottom: 8 }}>
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <Row align="middle" gutter={8}>
                    <Col>
                        <UserOutlined style={{ fontSize: 16 }} />
                    </Col>
                    <Col flex="auto">
                        <Space direction="vertical" size={2}>
                            <Text strong>{record.user.full_name}</Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>{record.user.email}</Text>
                            <Text type="secondary" style={{ fontSize: 11 }}>
                                <InfoCircleOutlined /> {record.properties.length} propert{record.properties.length === 1 ? 'y' : 'ies'}
                            </Text>
                        </Space>
                    </Col>
                    <Col>
                        <Tag color={record.selectedProperty.selected ? 'green' : 'default'} style={{ margin: 0 }}>
                            {record.selectedProperty.selected ? 'Active' : 'Inactive'}
                        </Tag>
                    </Col>
                </Row>

                <Select
                    value={selectedProperties[record.user.id] || record.selectedProperty.id}
                    onChange={(propertyId) => handlePropertySelect(record.user.id, propertyId)}
                    size="small"
                    style={{ width: '100%' }}
                    placeholder="Select property"
                >
                    {record.properties.map((property) => (
                        <Select.Option key={property.id} value={property.id}>
                            <Space direction="vertical" size={2}>
                                <Text>{property.name}</Text>
                                {property.defaultUri !== '-' && (
                                    <Text type="secondary" style={{ fontSize: 11 }}>{property.defaultUri}</Text>
                                )}
                            </Space>
                        </Select.Option>
                    ))}
                </Select>

                <Row justify="space-between" align="middle">
                    <Col>
                        {record.selectedProperty.measurementId !== 'N/A' && (
                            <Text code style={{ fontSize: 11 }}>
                                {record.selectedProperty.measurementId}
                            </Text>
                        )}
                    </Col>
                    <Col>
                        <Button
                            type="primary"
                            danger
                            size="small"
                            loading={loadingRemove[record.user.id]}
                            onClick={() => handleRemoveClick(record.user, record.selectedProperty)}
                        >
                            Remove
                        </Button>
                    </Col>
                </Row>
            </Space>
        </Card>
    );

    // Desktop table columns
    const desktopColumns = [
        {
            title: 'USER',
            dataIndex: 'user',
            key: 'user',
            width: 200,
            render: (_, record) => (
                <Space align="start">
                    <UserOutlined style={{ marginTop: 4 }} />
                    <Space direction="vertical" size={4}>
                        <Text strong>{record.user.full_name}</Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>{record.user.email}</Text>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                            <InfoCircleOutlined /> {record.properties.length} propert{record.properties.length === 1 ? 'y' : 'ies'}
                        </Text>
                    </Space>
                </Space>
            ),
        },
        {
            title: 'PROPERTY',
            dataIndex: 'selectedProperty',
            key: 'property',
            width: 250,
            render: (_, record) => (
                <Select
                    value={selectedProperties[record.user.id] || record.selectedProperty.id}
                    onChange={(propertyId) => handlePropertySelect(record.user.id, propertyId)}
                    style={{ width: '100%' }}
                    dropdownMatchSelectWidth={false}
                >
                    {record.properties.map((property) => (
                        <Select.Option key={property.id} value={property.id}>
                            <Space direction="vertical" size={2}>
                                <div>{property.name}</div>
                                {property.defaultUri !== '-' && (
                                    <Text type="secondary" style={{ fontSize: 11 }}>{property.defaultUri}</Text>
                                )}
                            </Space>
                        </Select.Option>
                    ))}
                </Select>
            ),
        },
        {
            title: 'STATUS',
            dataIndex: 'selectedProperty',
            key: 'status',
            width: 120,
            render: (_, record) => (
                <Space direction="vertical" size={4}>
                    <Badge
                        status={record.selectedProperty.selected ? 'success' : 'default'}
                        text={record.selectedProperty.selected ? 'Active' : 'Inactive'}
                    />
                    {record.selectedProperty.measurementId !== 'N/A' && (
                        <Text code style={{ fontSize: 12 }}>{record.selectedProperty.measurementId}</Text>
                    )}
                </Space>
            ),
        },
        {
            title: 'ACTIONS',
            key: 'actions',
            width: 100,
            render: (_, record) => (
                <Button
                    type="primary"
                    danger
                    size="small"
                    loading={loadingRemove[record.user.id]}
                    onClick={() => handleRemoveClick(record.user, record.selectedProperty)}
                >
                    Remove
                </Button>
            ),
        },
    ];

    return (
        <Card>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {/* Header Row with Title, Search, and Radio */}
                <Row gutter={[16, 16]} align="middle">
                    {/* Title Column */}
                    <Col xs={24} sm={24} md={8}>
                        <Space direction="vertical" size={0}>
                            <Title level={5} style={{ margin: 0 }}>GA4 Properties</Title>
                            <Paragraph type="secondary" style={{ margin: 0 }}>
                                Users: <strong>{groupedData.length}</strong> • Properties: <strong>{groupedData.reduce((acc, user) => acc + user.properties.length, 0)}</strong>
                                {(searchText || filterStatus !== 'a') && (
                                    <span> • Filtered: <strong>{filteredData.length}</strong></span>
                                )}
                            </Paragraph>
                        </Space>
                    </Col>

                    {/* Search Column */}
                    <Col xs={24} sm={24} md={8}>
                        <Input
                            placeholder="Search by user name, email, or property..."
                            prefix={<SearchOutlined />}
                            value={searchText}
                            onChange={handleSearchChange}
                            allowClear
                            size={screens.xs ? 'small' : 'middle'}
                        />
                    </Col>

                    {/* Radio Group Column */}
                    <Col xs={24} sm={24} md={8}>
                        <div style={{ textAlign: screens.md ? 'right' : 'left' }}>
                            <Radio.Group 
                                value={filterStatus} 
                                onChange={handleStatusFilterChange}
                                size={screens.xs ? 'small' : 'middle'}
                                optionType="button"
                                buttonStyle="solid"
                            >
                                <Radio.Button value="a">ALL ({groupedData.length})</Radio.Button>
                                <Radio.Button value="b">ACTIVE ({activeCount})</Radio.Button>
                                <Radio.Button value="c">INACTIVE ({inactiveCount})</Radio.Button>
                            </Radio.Group>
                        </div>
                    </Col>
                </Row>

                {/* Active Filters Display */}
                {(searchText || filterStatus !== 'a') && (
                    <Row>
                        <Col span={24}>
                            <Space size={8} wrap>
                                <Text type="secondary" style={{ fontSize: 12 }}>Active filters:</Text>
                                {filterStatus !== 'a' && (
                                    <Tag 
                                        closable 
                                        onClose={() => {
                                            setFilterStatus('a');
                                            if (typeof onChange === 'function') {
                                                onChange({ status: 'a', search: searchText });
                                            }
                                        }}
                                        style={{ marginRight: 0 }}
                                    >
                                        Status: {filterStatus === 'b' ? 'Active' : 'Inactive'}
                                    </Tag>
                                )}
                                {searchText && (
                                    <Tag 
                                        closable 
                                        onClose={() => {
                                            setSearchText('');
                                            if (typeof onChange === 'function') {
                                                onChange({ status: filterStatus, search: '' });
                                            }
                                        }}
                                        style={{ marginRight: 0 }}
                                    >
                                        Search: "{searchText}"
                                    </Tag>
                                )}
                                {(searchText || filterStatus !== 'a') && (
                                    <Button 
                                        type="link" 
                                        size="small" 
                                        onClick={clearFilters}
                                        style={{ padding: 0 }}
                                    >
                                        Clear all
                                    </Button>
                                )}
                            </Space>
                        </Col>
                    </Row>
                )}

                {/* Content Area */}
                {isDataEmpty ? (
                    <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                        <Title level={4}>
                            {searchText || filterStatus !== 'a' ? 'No Matching Results Found' : 'No GA4 Properties Found'}
                        </Title>
                        <Paragraph type="secondary">
                            {searchText && `No properties or users match "${searchText}". `}
                            {filterStatus === 'b' && 'No active properties found. '}
                            {filterStatus === 'c' && 'No inactive properties found. '}
                            Try adjusting your filters.
                        </Paragraph>
                        {(searchText || filterStatus !== 'a') && (
                            <Button onClick={clearFilters} type="primary">
                                Clear All Filters
                            </Button>
                        )}
                    </div>
                ) : (
                    <>
                        {!screens.md ? (
                            <div style={{ maxHeight: '500px', overflowY: 'auto', padding: '4px' }}>
                                {filteredData.map(record => (
                                    <MobileCard key={record.user.id} record={record} />
                                ))}
                            </div>
                        ) : (
                            <Table
                                dataSource={filteredData}
                                columns={desktopColumns}
                                rowKey={record => record.user.id}
                                pagination={{ 
                                    pageSize: 10,
                                    showSizeChanger: true,
                                    showQuickJumper: true,
                                    size: 'small',
                                }}
                                size="small"
                                scroll={{ x: screens.lg ? undefined : 800 }}
                                onChange={onChange}
                            />
                        )}
                    </>
                )}
            </Space>
        </Card>
    );
};